/**
 * Applying a move to the cached document, without waiting for the server.
 *
 * A drag used to cost a whole-document replacement: the mutation wrote the
 * server's doc into the cache with `() => data.doc`, so every piece got a
 * fresh object identity and the entire scene reconciled, chairs and all, every
 * time somebody let go of a table. And the drag preview was held until the
 * round trip finished, so there was a visible hitch on release.
 *
 * A move needs neither. The client already knows exactly where the piece
 * went, so it patches the cache itself and the request confirms it. This is
 * the ONE command the client is allowed to apply on its own, for a reason
 * worth stating: a move is a total assignment of three numbers, so applying
 * it cannot diverge from what the server will do with the same command.
 * Everything else, an add, a remove, a floor change, a re-spec, still takes
 * the server's document, because those touch the unplaced tray, the issue
 * list and fields the client would have to guess at.
 */
import type { VenueCommand, VenuePiece } from './mouse.types'

/**
 * Whether every command in a batch is a move.
 *
 * `every` and not `some`: a mixed batch is applied the slow, safe way,
 * because applying half of it optimistically would leave the cache in a state
 * no server response describes.
 */
export const isMovesOnly = (commands: readonly VenueCommand[]): boolean =>
  commands.length > 0 && commands.every((c) => c.op === 'movePieces')

/**
 * Move the pieces a batch names, in place.
 *
 * Written as a mutation because it runs against an immer draft inside
 * `updateQueryData`: mutating the one piece that moved leaves every other
 * piece's identity untouched, which is what lets the renderer skip them.
 *
 * `rotation` is only written when the move carries one, matching the server's
 * reducer: a nudge sends no rotation and must not reset it to zero.
 */
export const applyMoves = (
  pieces: VenuePiece[],
  commands: readonly VenueCommand[]
): void => {
  for (const command of commands) {
    if (command.op !== 'movePieces') continue
    for (const move of command.moves) {
      const piece = pieces.find((p) => p.id === move.id)
      // A piece the cache does not hold is not an error worth throwing over:
      // the server will refuse the stale batch and the page reloads.
      if (!piece) continue
      // Locked pieces are refused by the reducer, so applying the move here
      // would show something the server is about to reject.
      if (piece.locked) continue
      piece.x = move.x
      piece.z = move.z
      if (move.rotation !== undefined) piece.rotation = move.rotation
    }
  }
}
